import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Button, Pagination } from 'react-bootstrap';

export default function TablaLayout({
    title,
    data = [],
    columns,
    keyField,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onNavigate,
    navigateTitle = 'Navegar',
    navigateIcon = 'search',
    disableDelete = () => false,
    showConsult = true,
    showCreate = true,
    showEdit = true,
    breadcrumbs,
    itemsPerPage = 15,
    headerRight,
    page,
    onPageChange,
    highlightByKey,
    highlightValue,
    highlightDurationMs = 3000,
    scrollToHighlighted = true,
    highlightTick
}) {
    // Página controlada/no controlada
    const [currentPage, setCurrentPage] = useState(page ?? 1);
    useEffect(() => {
        if (typeof page === 'number' && page > 0 && page !== currentPage) {
            setCurrentPage(page);
        }
    }, [page, currentPage]); 

    const [isDesktop, setIsDesktop] = useState(
        typeof window !== 'undefined' ? window.innerWidth >= 992 : true
    );

    useEffect(() => {
        function handleResize() {
            setIsDesktop(window.innerWidth >= 992);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Anchos sugeridos por tipo de columna
    const getColumnWidth = (col) => {
        if (!isDesktop) return undefined;

        // ✅ PRIMERO: Respetar width personalizado si existe
        if (col.width) return col.width;

        if (col.key === 'index' || col.label === 'No' || col.label === 'ID') return '5%';

        if (col.key.includes('nombre') ||
            col.key.includes('ruta') ||
            col.label.toLowerCase().includes('nombre') ||
            col.label.toLowerCase().includes('apellido') ||
            col.label.toLowerCase().includes('email') ||
            col.label.toLowerCase().includes('conductor') ||
            col.label.toLowerCase().includes('cliente') ||
            col.label.toLowerCase().includes('ruta') ||
            col.label.toLowerCase().includes('usuario') ||
            col.label.toLowerCase().includes('operador')) return '20%';

        if (col.key.includes('ruta') ||
            col.key.includes('descripcion') ||
            col.key.includes('observacion') ||
            col.label.toLowerCase().includes('empresa') ||
            col.label.toLowerCase().includes('descripción') ||
            col.label.toLowerCase().includes('dirección') ||
            col.label.toLowerCase().includes('observación') ||
            col.label.toLowerCase().includes('componente') ||
            col.label.toLowerCase().includes('detalle')) return '30%';

        if (col.key.includes('url')) return '40%';

        if (col.key.includes('codigo') ||
            col.key === 'placa' ||
            col.key === 'manifiesto' ||
            col.key === 'nit' ||
            col.key === 'documento' ||
            col.key === 'cedula' ||
            col.key === 'celular' ||
            col.key === 'telefono' ||
            col.label.toLowerCase().includes('placa') ||
            col.label.toLowerCase().includes('manifiesto') ||
            col.label.toLowerCase().includes('código') ||
            col.label.toLowerCase().includes('nit') ||
            col.label.toLowerCase().includes('documento') ||
            col.label.toLowerCase().includes('cédula') ||
            col.label.toLowerCase().includes('celular') ||
            col.label.toLowerCase().includes('teléfono')) return '12%';

        if (col.key === 'estado' ||
            col.key === 'salida' ||
            col.key === 'llegada' ||
            col.key === 'activo' ||
            col.label.toLowerCase().includes('estado') ||
            col.label.toLowerCase().includes('salida') ||
            col.label.toLowerCase().includes('llegada') ||
            col.label.toLowerCase().includes('activo')) return '11%';

        return 'auto';
    };

    const trail = breadcrumbs || [
        { label: 'Inicio', href: '/inicio' },
        { label: title }
    ];

    const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
    const clampedPage = Math.min(Math.max(currentPage, 1), totalPages);
    const indexOfLast = clampedPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentItems = data.slice(indexOfFirst, indexOfLast);

    const changePage = (p) => {
        const next = Math.min(Math.max(p, 1), totalPages);
        setCurrentPage(next);
        if (typeof onPageChange === 'function') onPageChange(next);
    };

    // Paginación (máx. 15 visibles) y "..."
    const MAX_VISIBLE = 15;
    const { startPage, endPage, showLeftDots, showRightDots } = useMemo(() => {
        if (totalPages <= MAX_VISIBLE) {
            return { startPage: 1, endPage: totalPages, showLeftDots: false, showRightDots: false };
        }
        const half = Math.floor(MAX_VISIBLE / 2);
        let start = clampedPage - (MAX_VISIBLE % 2 === 0 ? half - 1 : half);
        let end = clampedPage + half;
        if (start < 1) {
            start = 1;
            end = MAX_VISIBLE;
        } else if (end > totalPages) {
            end = totalPages;
            start = totalPages - MAX_VISIBLE + 1;
        }
        return {
            startPage: start,
            endPage: end,
            showLeftDots: start > 1,
            showRightDots: end < totalPages
        };
    }, [clampedPage, totalPages]); 

    const hasHighlightedInPage = useMemo(() => {
        if (!highlightByKey || highlightValue == null) return false;
        return currentItems.some(
            (it) => String(it[highlightByKey]) === String(highlightValue)
        );
    }, [currentItems, highlightByKey, highlightValue]);

    // Ref a la fila resaltada para hacer scroll
    const highlightedRowRef = useRef(null);

    // Flash controlado: se activa cuando la fila está presente en la página
    const [flash, setFlash] = useState(false);
    useEffect(() => {
        if (!hasHighlightedInPage) return;
        setFlash(true);
        const t = setTimeout(() => setFlash(false), highlightDurationMs);
        return () => clearTimeout(t);
    }, [hasHighlightedInPage, highlightDurationMs, highlightTick]);

    // Scroll hasta la fila al entrar a la página correcta
    useEffect(() => {
        if (scrollToHighlighted && hasHighlightedInPage && highlightedRowRef.current) {
            highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [hasHighlightedInPage, currentItems, scrollToHighlighted]);

    return (
        <div className="d-flex flex-column" style={{ height: 'calc(90vh - 30px)' }}>
            {/* Header: miga de pan a la izquierda y slot a la derecha */}
            <div className="d-flex justify-content-between align-items-end mb-3" style={{ color: '#6c757d' }}>
                <div style={{ fontSize: '1.5rem' }}>
                    {trail.map((crumb, i) => (
                        <React.Fragment key={i}>
                            {crumb.href ? (
                                <a href={crumb.href} style={{ color: '#0d6efd', textDecoration: 'none' }}>
                                    {crumb.label}
                                </a>
                            ) : (
                                <span>{crumb.label}</span>
                            )}
                            {i < trail.length - 1 && <span style={{ margin: '0 0.5rem' }}>/</span>}
                        </React.Fragment>
                    ))}
                </div>

                {headerRight && (
                    <div className="ms-auto d-flex align-items-end justify-content-end" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                        {headerRight}
                    </div>
                )}
            </div>

            {/* Tabla */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: isDesktop ? 'hidden' : 'auto'
                }}
            >
                <Table
                    striped
                    style={{
                        width: isDesktop ? '100%' : 'auto',
                        tableLayout: isDesktop ? 'fixed' : 'auto',
                        minWidth: !isDesktop ? 'max-content' : undefined
                    }}
                >
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: col.align === 'center' ? 'center' : 'left',
                                        whiteSpace: 'nowrap',
                                        width: getColumnWidth(col)
                                    }}
                                >
                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {col.label}
                                    </div>
                                </th>
                            ))}
                            {(showCreate || showConsult || showEdit || onNavigate) && (
                                <th
                                    style={{
                                        padding: '0.75rem',
                                        textAlign: 'right',
                                        whiteSpace: 'nowrap',
                                        width: '120px'
                                    }}
                                >
                                    {showCreate && onCreate && (
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            title="Crear"
                                            onClick={onCreate}
                                        >
                                            <span className="material-icons">note_add</span>
                                        </Button>
                                    )}
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((item, idx) => {
                                const displayIndex = indexOfFirst + idx + 1;
                                const isHighlighted =
                                    highlightByKey &&
                                    highlightValue != null &&
                                    String(item[highlightByKey]) === String(highlightValue);

                                const trStyle = isHighlighted && flash
                                    ? {
                                        height: '60px',
                                        backgroundColor: '#cfe2ff',
                                        '--bs-table-accent-bg': '#cfe2ff'
                                    }
                                    : { height: '60px' };
                                const tdHighlight = isHighlighted && flash ? { backgroundColor: '#cfe2ff' } : undefined;

                                return (
                                    <tr
                                        key={item[keyField] ?? idx}
                                        ref={isHighlighted ? highlightedRowRef : null}
                                        style={trStyle}
                                    >
                                        {columns.map(col => {
                                            const content =
                                                col.key === 'index'
                                                    ? displayIndex
                                                    : col.render
                                                        ? col.render(item[col.key], displayIndex, item)
                                                        : item[col.key];
                                            return (
                                                <td
                                                    key={col.key}
                                                    style={{
                                                        padding: '0.75rem',
                                                        textAlign: col.align === 'center' ? 'center' : 'left',
                                                        whiteSpace: 'nowrap',
                                                        width: getColumnWidth(col),
                                                        ...tdHighlight
                                                    }}
                                                    title={typeof content === 'string' ? content : undefined}
                                                >
                                                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {content}
                                                    </div>
                                                </td>
                                            );
                                        })}

                                        {(showCreate || showConsult || showEdit || onNavigate) && (
                                            <td
                                                style={{
                                                    padding: '0.75rem',
                                                    textAlign: 'right',
                                                    whiteSpace: 'nowrap',
                                                    width: '120px',
                                                    ...tdHighlight
                                                }}
                                            >
                                                <div className="d-flex" style={{ gap: '0.25rem', justifyContent: 'flex-end' }}>
                                                    {onNavigate ? (
                                                        <Button
                                                            variant="outline-info"
                                                            size="sm"
                                                            title={navigateTitle}
                                                            onClick={() => onNavigate(item)}
                                                        >
                                                            <span className="material-icons">{navigateIcon}</span>
                                                        </Button>
                                                    ) : (
                                                        showConsult && onView && (
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                title="Consultar"
                                                                onClick={() => onView(item)}
                                                            >
                                                                <span className="material-icons">search</span>
                                                            </Button>
                                                        )
                                                    )}

                                                    {onEdit && showEdit && (
                                                        <Button
                                                            variant="outline-warning"
                                                            size="sm"
                                                            title="Editar"
                                                            onClick={() => onEdit(item)}
                                                        >
                                                            <span className="material-icons">edit</span>
                                                        </Button>
                                                    )}

                                                    {onDelete && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            title="Borrar"
                                                            disabled={disableDelete(item)}
                                                            onClick={() => onDelete(item[keyField])}
                                                        >
                                                            <span className="material-icons">delete</span>
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={columns.length + 1} style={{ padding: '0.75rem', textAlign: 'left' }}>
                                    No hay información.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Paginación */}
            <div className="d-flex justify-content-center mt-2" style={{ overflowX: 'auto' }}>
                <Pagination className="mb-0">
                    <Pagination.First disabled={clampedPage === 1} onClick={() => changePage(1)} />
                    <Pagination.Prev disabled={clampedPage === 1} onClick={() => changePage(clampedPage - 1)} />

                    {showLeftDots && <Pagination.Ellipsis disabled />}

                    {Array.from({ length: endPage - startPage + 1 }).map((_, i) => {
                        const p = startPage + i;
                        return (
                            <Pagination.Item key={p} active={p === clampedPage} onClick={() => changePage(p)}>
                                {p}
                            </Pagination.Item>
                        );
                    })}

                    {showRightDots && <Pagination.Ellipsis disabled />}

                    <Pagination.Next disabled={clampedPage === totalPages} onClick={() => changePage(clampedPage + 1)} />
                    <Pagination.Last disabled={clampedPage === totalPages} onClick={() => changePage(totalPages)} />
                </Pagination>
            </div>
        </div>
    );
}
